<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ConversationMergeModal from './ConversationMergeModal.vue';
import ConversationMoveModal from './ConversationMoveModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import { vOnClickOutside } from '@vueuse/components';
import mutationTypes from 'dashboard/store/mutation-types';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
  CMD_TRANSLATION_COMPLETED,
} from 'dashboard/helper/commandbar/events';
import ConfirmationModal from '../modal/ConfirmationModal.vue';

export default {
  components: {
    EmailTranscriptModal,
    ConversationMergeModal,
    ConversationMoveModal,
    ResolveAction,
    ButtonV4,
    ConfirmationModal,
  },
  directives: {
    onClickOutside: vOnClickOutside,
  },
  data() {
    return {
      showEmailActionsModal: false,
      showMergeModal: false,
      showMoveModal: false,
      showOverflowMenu: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      getAccount: 'accounts/getAccount',
      currentAccountId: 'getCurrentAccountId',
      getUISettings: 'getUISettings',
      getInboxById: 'inboxes/getInboxById',
      getContact: 'contacts/getContact',
    }),
    isExpandedLayout() {
      const { conversation_display_type: displayType } = this.getUISettings;
      return displayType === 'expanded';
    },
    showMoveConversation() {
      const inbox = this.getInboxById(this.currentChat.inbox_id);
      const channelType = inbox?.channelType;
      return (
        channelType === INBOX_TYPES.EMAIL || channelType === INBOX_TYPES.WEB
      );
    },
    isMoveDisabled() {
      const inbox = this.getInboxById(this.currentChat.inbox_id);
      if (inbox?.channelType !== INBOX_TYPES.WEB) return false;
      const contactId = this.currentChat.meta?.sender?.id;
      const contact = contactId ? this.getContact(contactId) : {};
      return !contact.email;
    },
    moveTooltip() {
      if (this.isMoveDisabled) {
        return this.$t('CONTACT_PANEL.MOVE_CONVERSATION_MISSING_EMAIL');
      }
      return this.$t('CONTACT_PANEL.MOVE_CONVERSATION');
    },
    isContactDomainWhitelisted() {
      return this.currentChat.domain_whitelisted;
    },
    contactEmail() {
      return this.currentChat.meta?.sender?.email || '';
    },
    contactDomain() {
      return this.contactEmail.split('@').pop();
    },
    isAdmin() {
      return useAdmin();
    },
    isContactDomainBlocked() {
      return this.currentChat.domainBlocked;
    },
    isTranslating() {
      return this.currentChat.isTranslating || false;
    },
    muteIcon() {
      return this.currentChat.muted
        ? 'i-lucide-volume-off'
        : 'i-lucide-volume-1';
    },
    muteLabel() {
      return this.currentChat.muted
        ? this.$t('CONTACT_PANEL.UNMUTE_CONTACT')
        : this.$t('CONTACT_PANEL.MUTE_CONTACT');
    },
    blockDomainLabel() {
      return this.isContactDomainBlocked
        ? this.$t('CONTACT_PANEL.UNBLOCK_CONTACT_DOMAIN')
        : this.$t('CONTACT_PANEL.BLOCK_CONTACT_DOMAIN');
    },
    blockDomainIcon() {
      return this.isContactDomainBlocked
        ? 'i-lucide-megaphone-off'
        : 'i-lucide-megaphone';
    },
    muteTooltip() {
      if (this.isContactDomainBlocked) {
        return this.$t('CONTACT_PANEL.MUTE_CONTACT_DISABLED');
      }
      return this.muteLabel;
    },
    blockDomainTooltip() {
      return this.blockDomainLabel;
    },
    actionItems() {
      return [
        {
          show: this.showMoveConversation,
          icon: 'i-lucide-mailbox',
          tooltip: this.moveTooltip,
          label: this.$t('CONTACT_PANEL.MOVE_CONVERSATION'),
          disabled: this.isMoveDisabled,
          action: this.toggleMoveModal,
        },
        {
          show:
            this.isAdmin &&
            !!this.currentChat.additional_attributes?.jarabot_url,
          icon: 'i-lucide-bot',
          tooltip: this.$t('CONTACT_PANEL.OPEN_JARABOT'),
          label: this.$t('CONTACT_PANEL.OPEN_JARABOT'),
          action: this.openJarabot,
        },
        {
          show: true,
          icon: 'i-lucide-languages',
          tooltip: this.$t('CONTACT_PANEL.TRANSLATE_CONVERSATION'),
          label: this.$t('CONTACT_PANEL.TRANSLATE_CONVERSATION'),
          disabled: this.isTranslating,
          action: this.translateConversation,
        },
        {
          show: true,
          icon: 'i-lucide-merge',
          tooltip: this.$t('CONTACT_PANEL.MERGE_CONVERSATION'),
          label: this.$t('CONTACT_PANEL.MERGE_CONVERSATION'),
          action: this.toggleMergeModal,
        },
        {
          show: true,
          icon: this.muteIcon,
          tooltip: this.muteTooltip,
          label: this.muteLabel,
          disabled: this.isContactDomainBlocked,
          action: this.toggleMute,
        },
        {
          show: this.isAdmin,
          icon: this.blockDomainIcon,
          tooltip: this.blockDomainTooltip,
          label: this.blockDomainLabel,
          action: this.toggleDomainBlock,
        },
        {
          show: true,
          icon: 'i-lucide-share',
          tooltip: this.$t('CONTACT_PANEL.SEND_TRANSCRIPT'),
          label: this.$t('CONTACT_PANEL.SEND_TRANSCRIPT'),
          action: this.toggleEmailActionsModal,
        },
      ].filter(item => item.show);
    },
  },
  mounted() {
    emitter.on(CMD_MUTE_CONVERSATION, this.mute);
    emitter.on(CMD_UNMUTE_CONVERSATION, this.unmute);
    emitter.on(CMD_SEND_TRANSCRIPT, this.toggleEmailActionsModal);
    emitter.on(CMD_TRANSLATION_COMPLETED, this.onTranslationCompleted);
  },
  unmounted() {
    emitter.off(CMD_MUTE_CONVERSATION, this.mute);
    emitter.off(CMD_UNMUTE_CONVERSATION, this.unmute);
    emitter.off(CMD_SEND_TRANSCRIPT, this.toggleEmailActionsModal);
    emitter.off(CMD_TRANSLATION_COMPLETED, this.onTranslationCompleted);
  },
  methods: {
    mute() {
      this.$store.dispatch('muteConversation', this.currentChat.id);
      useAlert(this.$t('CONTACT_PANEL.MUTED_SUCCESS'));
    },
    unmute() {
      this.$store.dispatch('unmuteConversation', this.currentChat.id);
      useAlert(this.$t('CONTACT_PANEL.UNMUTED_SUCCESS'));
    },
    async toggleMute() {
      const result = await this.$refs.muteConfirmationModal.showConfirmation();
      if (!result) return;

      if (this.currentChat.muted) {
        this.unmute();
      } else {
        this.mute();
      }
    },
    translateConversation() {
      if (this.isTranslating) return;

      const { locale } = this.getAccount(this.currentAccountId);

      this.$store
        .dispatch('translateConversation', {
          conversationId: this.currentChat.id,
          targetLanguage: locale || 'en',
        })
        .then(() => {
          useAlert(this.$t('CONTACT_PANEL.TRANSLATE_INITIATED'));
        })
        .catch(() => {
          useAlert(this.$t('CONTACT_PANEL.TRANSLATE_ERROR'));
        });
    },
    async blockContactDomain() {
      try {
        await this.$store.dispatch(
          'muteConversationContactDomain',
          this.currentChat.id
        );
        useAlert(this.$t('CONTACT_PANEL.CONTACT_DOMAIN_BLOCKED_SUCCESS'));
      } catch (error) {
        useAlert(
          error?.response?.data?.error ||
            this.$t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_ERROR_MESSAGE')
        );
      }
    },
    unblockContactDomain() {
      this.$store.dispatch(
        'unmuteConversationContactDomain',
        this.currentChat.id
      );
      useAlert(this.$t('CONTACT_PANEL.CONTACT_DOMAIN_UNBLOCKED_SUCCESS'));
    },
    toggleEmailActionsModal() {
      this.showEmailActionsModal = !this.showEmailActionsModal;
    },
    toggleMergeModal() {
      this.showMergeModal = !this.showMergeModal;
    },
    toggleMoveModal() {
      this.showMoveModal = !this.showMoveModal;
    },
    async toggleDomainBlock() {
      // A public (freemail) domain is shared by unrelated senders, so it can
      // never be blocked as a whole. Offer to block the single address instead.
      if (!this.isContactDomainBlocked && this.isContactDomainWhitelisted) {
        const blockAddress =
          await this.$refs.publicDomainModal.showConfirmation();
        if (blockAddress) this.mute();
        return;
      }

      const result = await this.$refs.confirmationModal.showConfirmation();
      if (!result) return;

      if (this.isContactDomainBlocked) {
        this.unblockContactDomain();
      } else {
        this.blockContactDomain();
      }
    },
    onTranslationCompleted(data) {
      const conversationId = data.conversation.id;

      this.$store.commit(mutationTypes.SET_CONVERSATION_TRANSLATE_LOADING, {
        conversationId,
        loading: false,
      });

      useAlert(this.$t('CONTACT_PANEL.TRANSLATE_SUCCESS'));
    },
    openJarabot() {
      const url = this.currentChat.additional_attributes?.jarabot_url;
      if (url) {
        window.open(url, '_blank');
      }
    },
    toggleOverflowMenu() {
      this.showOverflowMenu = !this.showOverflowMenu;
    },
    closeOverflowMenu() {
      this.showOverflowMenu = false;
    },
    overflowAction(action) {
      this.closeOverflowMenu();
      action();
    },
  },
};
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <div
      class="items-center gap-1"
      :class="
        isExpandedLayout
          ? 'hidden min-[1050px]:flex'
          : 'hidden min-[1400px]:flex'
      "
    >
      <span
        v-for="item in actionItems"
        :key="item.icon"
        v-tooltip="item.tooltip"
      >
        <ButtonV4
          size="sm"
          variant="ghost"
          color="slate"
          :icon="item.icon"
          :disabled="item.disabled"
          @click="item.action"
        />
      </span>
    </div>

    <div
      v-on-click-outside="closeOverflowMenu"
      class="relative"
      :class="isExpandedLayout ? 'min-[1050px]:hidden' : 'min-[1400px]:hidden'"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-ellipsis-vertical"
        :class="{ 'bg-n-alpha-2': showOverflowMenu }"
        @click="toggleOverflowMenu"
      />
      <ul
        v-if="showOverflowMenu"
        class="absolute right-0 top-10 z-50 min-w-[200px] rounded-xl border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] py-2 px-2 shadow-lg list-none reset-base grid gap-1"
      >
        <li
          v-for="item in actionItems"
          :key="item.icon"
          v-tooltip="item.disabled ? item.tooltip : ''"
        >
          <button
            class="flex items-center gap-3 w-full p-2 text-sm text-n-slate-12 rounded-lg hover:bg-n-alpha-2 border-0 reset-base text-left"
            :disabled="item.disabled"
            @click="overflowAction(item.action)"
          >
            <span class="size-4 text-n-slate-11" :class="item.icon" />
            {{ item.label }}
          </button>
        </li>
      </ul>
    </div>

    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailActionsModal"
    />
    <ConversationMoveModal
      v-if="showMoveModal"
      :show="showMoveModal"
      @close="toggleMoveModal"
    />
    <ConversationMergeModal
      v-if="showMergeModal"
      :show="showMergeModal"
      @close="toggleMergeModal"
    />
    <ConfirmationModal
      ref="muteConfirmationModal"
      :title="
        currentChat.muted
          ? $t('CONTACTS_LAYOUT.HEADER.UNBLOCK_CONTACT')
          : $t('CONTACTS_LAYOUT.HEADER.BLOCK_CONTACT')
      "
      :description="
        currentChat.muted
          ? $t('CONTACTS_LAYOUT.HEADER.UNBLOCK_CONTACT_MESSAGE')
          : $t('CONTACTS_LAYOUT.HEADER.BLOCK_CONTACT_MESSAGE')
      "
      :confirm-label="$t('CONTACT_PANEL.CONFIRM.YES')"
      :cancel-label="$t('CONTACT_PANEL.CONFIRM.CANCEL')"
    />
    <ConfirmationModal
      ref="confirmationModal"
      :title="
        isContactDomainBlocked
          ? $t('CONTACT_PANEL.UNBLOCK_CONTACT_DOMAIN')
          : $t('CONTACT_PANEL.BLOCK_CONTACT_DOMAIN')
      "
      :description="
        isContactDomainBlocked
          ? $t('CONTACT_PANEL.UNBLOCK_CONTACT_DOMAIN_MESSAGE')
          : $t('CONTACT_PANEL.BLOCK_CONTACT_DOMAIN_MESSAGE')
      "
      :confirm-label="$t('CONTACT_PANEL.CONFIRM.YES')"
      :cancel-label="$t('CONTACT_PANEL.CONFIRM.CANCEL')"
    />
    <ConfirmationModal
      ref="publicDomainModal"
      :title="$t('CONTACT_PANEL.BLOCK_PUBLIC_DOMAIN_TITLE')"
      :description="
        $t('CONTACT_PANEL.BLOCK_PUBLIC_DOMAIN_MESSAGE', {
          domain: contactDomain,
          email: contactEmail,
        })
      "
      :confirm-label="$t('CONTACT_PANEL.BLOCK_ONLY_THIS_ADDRESS')"
      :cancel-label="$t('CONTACT_PANEL.CONFIRM.CANCEL')"
    />
  </div>
</template>

<style scoped lang="scss">
.more--button {
  @apply items-center flex ml-2 rtl:ml-0 rtl:mr-2;
}

.dropdown-pane {
  @apply -right-2 top-12;
}

.icon {
  @apply mr-1 rtl:mr-0 rtl:ml-1 min-w-[1rem];
}
</style>
